import { Button, Grid, Paper } from '@mui/material';
import { SyntheticEvent, useEffect, useRef, useState } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { Canvas, NodeData, Node, Edge, Remove, CanvasRef } from 'reaflow';
import DeleteConfirmationDialog from '../../components/DeleteConfirmationDialog';
import InfoPopup from '../../components/InfoPopup';
import { AnalyzerDish } from '../../types';
import { useAnalyzer } from '../analyzers/api/getAnalyzer';
import { useDish } from '../dishes/api/getDish';
import DishesDialog from '../dishes/DishesDialog';
import PairAnalyzerDishDialog from '../dishes/PairAnalyzerDishDialog';
import ConnectInstrumentDialog from '../instrumentConfiguration/ConnectInstrumentDialog';
import GroupInstrumentsDialog from '../instrumentConfiguration/GroupInstrumentsDialog';
import { useLocation } from '../locations/api/getLocation';
import LocationDialog from '../locations/LocationDialog';
import { useSatellite } from '../satellites/api/getSatellite';
import SatelliteDialog from '../satellites/SatelliteDialog';
import { useSwitches } from '../switches/api/getSwitches';
import { useSystemOverview } from './api/getSystemOverview';
import { useLinkNodeHandler } from './hooks/useLinkNodeHandler';
import { useNodeDeleteHandler } from './hooks/useNodeDeleteHandler';
import { useSystemOverviewGraph } from './hooks/useSystemOverviewGraph';
import { useSystemOverviewTree } from './hooks/useSystemOverviewTree';
import { useUnlinkNodesHandler } from './hooks/useUnlinkNodesHandler';
import SystemOverviewLegend from './SystemOverviewLegend';
import SystemOverviewMenu from './SystemOverviewMenu';
import { checkIfEditDeleteDisabled } from './utils/nodeHandlers';

const SystemOverviewPage = () => {
  const { data: systemOverview } = useSystemOverview({});
  const ref = useRef<CanvasRef | null>(null);
  const legendRef = useRef<HTMLElement | null>(null);
  const [selectedNode, setSelectedNode] = useState<string>('');
  const selectedNodeId = Number(selectedNode.split('-')[1]);
  const selectedSatelliteId = selectedNode.startsWith('satellite-')
    ? selectedNodeId
    : -1;
  const { data: satellite } = useSatellite({
    satelliteId: selectedSatelliteId,
  });
  const selectedDishId = selectedNode.startsWith('dish-') ? selectedNodeId : -1;
  const { data: dish } = useDish({ dishId: selectedDishId });
  const selectedAnalyzerId = selectedNode.startsWith('analyzer-')
    ? selectedNodeId
    : -1;
  const { data: analyzer } = useAnalyzer({ analyzerId: selectedAnalyzerId });
  const selectedSwitchId = selectedNode.startsWith('switch-')
    ? selectedNodeId
    : -1;
  const { data: switches } = useSwitches({});
  const selectedSwitch = switches?.find((item) => item.id === selectedSwitchId);
  const selectedLocationId = selectedNode.startsWith('location-')
    ? selectedNodeId
    : -1;
  const { data: selectedLocation } = useLocation({
    locationId: selectedLocationId,
  });
  const selectedItemName = dish
    ? dish.name
    : analyzer
    ? analyzer.name
    : selectedSwitch
    ? selectedSwitch.name
    : selectedLocation
    ? selectedLocation.name
    : satellite
    ? satellite.name
    : '';

  const [editDialogOpen, setEditDialogOpen] = useState<boolean>(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [analyzerDish, setAnalyzerDish] = useState<
    undefined | Omit<AnalyzerDish, 'id'>
  >(undefined);
  const [analyzerIdToPair, setAnalyzerIdToPair] = useState<number>(-1);
  const [zoomFactor, setZoomFactor] = useState<number>(0);
  const [snackbar, setSnackBar] = useState<{
    severity: 'success' | 'error';
    open: boolean;
    message: string;
  }>({
    severity: 'success',
    open: false,
    message: '',
  });
  const handleSelectNode = (
    event: SyntheticEvent,
    nodeId: string[] | string
  ) => {
    event.stopPropagation();
    if (typeof nodeId === 'string') setSelectedNode(nodeId);
    else if (typeof nodeId === 'object' && nodeId && nodeId.length > 0)
      setSelectedNode(nodeId[0]);
  };
  const handleClickNode = (event: any, node: NodeData<any>) => {
    setEditDialogOpen(false);
    setDeleteDialogOpen(false);
    setSelectedNode(node.id);
  };
  const { handleLinkNodes } = useLinkNodeHandler(
    setSelectedNode,
    setAnalyzerDish,
    setAnalyzerIdToPair
  );
  const handleUnlinkNodes = useUnlinkNodesHandler(selectedNode);
  const handleDeleteNode = useNodeDeleteHandler(
    dish,
    analyzer,
    selectedSwitch,
    selectedLocation,
    satellite
  );
  const SystemOverviewTree = useSystemOverviewTree(
    selectedNode,
    handleSelectNode,
    systemOverview,
    handleLinkNodes
  );
  const handleMenuItemClicked = (selectedItem: string) => {
    setSelectedNode(selectedItem + '-0');
    setEditDialogOpen(true);
  };

  // Listen for size changes to the legend as a proxy for changing layouts, and then
  // redraw the graph after 100ms (to make sure the DOM is updated) to avoid edges
  // being positioned incorrectly after layout changes
  useEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      setTimeout(() => {
        if (ref?.current?.fitCanvas) ref.current.fitCanvas();
      }, 100);
    });
    if (legendRef.current) resizeObserver.observe(legendRef.current);
    return () => resizeObserver.disconnect();
  }, []);

  const { nodes, edges } = useSystemOverviewGraph();
  return (
    <DndProvider backend={HTML5Backend}>
      <Grid item container>
        <Grid item container component={Paper} sx={{ height: '85vh' }}>
          <Grid
            item
            xs={3}
            container
            sx={{
              p: '1rem',
              overflow: 'auto',
              maxHeight: '100%',
            }}
            direction='column'
            onClick={() => setSelectedNode('')}
          >
            <Grid item container spacing={1} sx={{ mb: '.8rem' }}>
              <SystemOverviewMenu
                handleMenuItemClicked={handleMenuItemClicked}
              />
              <Grid item>
                <Button
                  disabled={checkIfEditDeleteDisabled(selectedNode)}
                  color='secondary'
                  variant='contained'
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditDialogOpen(true);
                  }}
                >
                  Edit
                </Button>
              </Grid>
              <Grid item>
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    setDeleteDialogOpen(true);
                  }}
                  disabled={checkIfEditDeleteDisabled(selectedNode)}
                  color='error'
                  variant='contained'
                >
                  Delete
                </Button>
              </Grid>
            </Grid>
            {SystemOverviewTree}
          </Grid>
          <Grid
            item
            xs={9}
            container
            sx={{
              height: `calc(100% - ${
                legendRef.current?.clientHeight + 'px' || '0px'
              })`,
            }}
          >
            <SystemOverviewLegend
              canvasRef={ref?.current}
              zoomFactor={zoomFactor}
              setZoomFactor={setZoomFactor}
              legendRef={legendRef}
            />
            <Canvas
              nodes={nodes}
              edges={edges}
              ref={ref}
              pannable={true}
              selections={[selectedNode]}
              onCanvasClick={(e) => {
                // The nodeName is 'g' when the user has dragged a connection between nodes. We only
                // want to reset the selectedNode (i.e., deselect all nodes) when the user clicks somewhere
                // without nodes.
                // @ts-ignore
                if (e.target.nodeName !== 'g') setSelectedNode('');
              }}
              onNodeLink={(event, from, to) => {
                handleLinkNodes(from, to);
              }}
              zoom={1}
              node={
                <Node
                  onClick={handleClickNode}
                  remove={
                    <Remove
                      className='removeBox'
                      onClick={() => setDeleteDialogOpen(true)}
                    />
                  }
                />
              }
              edge={
                <Edge
                  onClick={handleClickNode}
                  remove={<Remove onClick={handleUnlinkNodes} />}
                />
              }
            />
          </Grid>
        </Grid>
      </Grid>
      <DishesDialog
        open={editDialogOpen && selectedDishId !== -1}
        handleClose={() => setEditDialogOpen(false)}
        dish={dish}
      />
      <ConnectInstrumentDialog
        open={
          editDialogOpen &&
          (selectedAnalyzerId !== -1 || selectedSwitchId !== -1)
        }
        handleInstrumentTypeChanged={() => {}}
        handleClose={() => setEditDialogOpen(false)}
        setSnackBar={setSnackBar}
        selectedInstrument={
          selectedAnalyzerId !== -1 ? analyzer : selectedSwitch
        }
        selectedInstrumentType={selectedAnalyzerId !== -1 ? 0 : 1}
      />
      <LocationDialog
        open={editDialogOpen && selectedLocationId !== -1}
        handleClose={() => setEditDialogOpen(false)}
        location={selectedLocation}
        setSnackBar={setSnackBar}
      />
      <PairAnalyzerDishDialog
        open={!!analyzerDish}
        handleClose={() => setAnalyzerDish(undefined)}
        analyzerDish={analyzerDish}
        dish={dish}
      />
      <GroupInstrumentsDialog
        open={analyzerIdToPair !== -1}
        handleClose={() => setAnalyzerIdToPair(-1)}
        selectedAnalyzerId={analyzerIdToPair}
      />
      <SatelliteDialog
        open={editDialogOpen && selectedSatelliteId !== -1}
        handleClose={() => setEditDialogOpen(false)}
        satellite={satellite}
      />
      <InfoPopup
        openCondition={snackbar.open}
        title={null}
        message={snackbar.message}
        error={snackbar.severity === 'error'}
        success={snackbar.severity === 'success'}
        handleClose={() => setSnackBar({ ...snackbar, open: false })}
      />
      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        itemToDelete={selectedItemName}
        onSubmit={() => {
          handleDeleteNode();
        }}
        onClose={() => setDeleteDialogOpen(false)}
      />
    </DndProvider>
  );
};

export default SystemOverviewPage;
